import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Star, MapPin, Phone, Globe, Mail, ExternalLink } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Card, CardContent } from '@/components/atoms/card';
import { Separator } from '@/components/atoms/separator';
import { PublicLayout } from '@/components/public/PublicLayout';
import { publicGet } from '@/lib/api/publicClient';
import { type PublicBusinessProfile } from '@/types/public';

export const Route = createFileRoute('/(public)/google-business')({
  component: GoogleBusinessPage,
});

function StarRating({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${
            i < Math.round(value)
              ? 'fill-yellow-400 text-yellow-400'
              : 'fill-gray-200 text-gray-200'
          }`}
        />
      ))}
    </div>
  );
}

function GoogleBusinessPage() {
  const { data: profile, isLoading } = useQuery<PublicBusinessProfile>({
    queryKey: ['public', 'google-business'],
    queryFn: () => publicGet<PublicBusinessProfile>('/api/public/business-profile'),
    retry: false,
  });

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </PublicLayout>
    );
  }

  // Show a sensible fallback even if the API isn't configured yet
  const biz = profile ?? {
    name: 'Our Business',
    description: 'We provide excellent service to our customers.',
  };

  return (
    <PublicLayout>
      <div className="space-y-4">
        {/* Cover / Hero */}
        {biz.coverImage && (
          <div className="overflow-hidden rounded-xl">
            <img
              src={biz.coverImage}
              alt={biz.name}
              className="h-40 w-full object-cover sm:h-52"
              loading="lazy"
            />
          </div>
        )}

        {/* Business card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              {biz.logo ? (
                <img
                  src={biz.logo}
                  alt={biz.name}
                  className="h-16 w-16 rounded-xl border object-contain shadow-sm"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white text-2xl font-bold">
                  {biz.name[0]}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-gray-900">{biz.name}</h1>
                {biz.tagline && <p className="mt-0.5 text-sm text-gray-500">{biz.tagline}</p>}
                {biz.averageRating != null && (
                  <div className="mt-2 flex items-center gap-2">
                    <StarRating value={biz.averageRating} />
                    <span className="text-sm font-medium text-gray-700">
                      {biz.averageRating.toFixed(1)}
                    </span>
                    {biz.totalReviews != null && (
                      <span className="text-sm text-gray-400">({biz.totalReviews} reviews)</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {biz.description && (
              <>
                <Separator className="my-4" />
                <p className="text-sm text-gray-600">{biz.description}</p>
              </>
            )}

            <Separator className="my-4" />

            {/* Contact details */}
            <div className="space-y-2.5 text-sm">
              {biz.address && (
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="h-4 w-4 shrink-0 text-gray-400" />
                  <span>{biz.address}</span>
                </div>
              )}
              {biz.phone && (
                <a
                  href={`tel:${biz.phone}`}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-600"
                >
                  <Phone className="h-4 w-4 shrink-0 text-gray-400" />
                  <span>{biz.phone}</span>
                </a>
              )}
              {biz.email && (
                <a
                  href={`mailto:${biz.email}`}
                  className="flex items-center gap-2 text-gray-600 hover:text-blue-600"
                >
                  <Mail className="h-4 w-4 shrink-0 text-gray-400" />
                  <span>{biz.email}</span>
                </a>
              )}
              {biz.website && (
                <a
                  href={biz.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                >
                  <Globe className="h-4 w-4 shrink-0 text-gray-400" />
                  <span>{biz.website.replace(/^https?:\/\//, '')}</span>
                </a>
              )}
            </div>

            {/* Services */}
            {biz.services && biz.services.length > 0 && (
              <>
                <Separator className="my-4" />
                <div>
                  <p className="mb-2 text-sm font-medium text-gray-700">Services</p>
                  <div className="flex flex-wrap gap-2">
                    {biz.services.map((service) => (
                      <span
                        key={service}
                        className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700"
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Leave a Google review CTA */}
        {biz.googleReviewUrl && (
          <Card>
            <CardContent className="py-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-gray-900">Enjoyed our service?</p>
                  <p className="text-sm text-gray-500">Leave us a review on Google</p>
                </div>
                <Button asChild size="sm">
                  <a href={biz.googleReviewUrl} target="_blank" rel="noopener noreferrer">
                    <Star className="mr-2 h-4 w-4" />
                    Review Us
                    <ExternalLink className="ml-2 h-3 w-3" />
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PublicLayout>
  );
}
